import { useRef, useState } from 'react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverItem,
  AnchoredPopoverTrigger,
} from '@scripsteam/scrips-signal-ds';
import { MoreHorizontal } from 'lucide-react';
import { SectionIconAction } from '../section-icon-action';
import { SaveAsOrderSetDialog, type SaveAsOrderSetDialogProps } from './save-as-order-set-dialog';

/**
 * The A/P header's ⋯ "More A/P actions" (PROD-2722, v3 board 7), holding "Save as order set".
 * The trigger is the header's icon button (`SectionIconAction`), passed through the popover trigger's
 * `render` so it is replaced, not wrapped. The surface portals next to the trigger (the
 * `position: relative` host) so the app's root zoom does not throw it off its anchor, the same
 * as the rail's account menu (`app/nav-footer-slot.tsx`).
 *
 * The host renders this only with order sets on (`VITE_ORDER_SETS_APPLY`) for a physician
 * (`mayOrder`); see `canApplyOrderSets`.
 */
export type SaveAsOrderSetActionProps = Omit<SaveAsOrderSetDialogProps, 'open' | 'onOpenChange'>;

export function SaveAsOrderSetAction(props: SaveAsOrderSetActionProps) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <span ref={hostRef} className="relative inline-flex">
      <AnchoredPopover open={menuOpen} onOpenChange={setMenuOpen}>
        <AnchoredPopoverTrigger
          render={<SectionIconAction icon={MoreHorizontal} label="More A/P actions" aria-haspopup="menu" />}
        />
        <AnchoredPopoverContent container={hostRef} side="bottom" align="end" sideOffset={4}>
          <AnchoredPopoverItem
            onClick={() => {
              setMenuOpen(false);
              setDialogOpen(true);
            }}
          >
            Save as order set
          </AnchoredPopoverItem>
        </AnchoredPopoverContent>
      </AnchoredPopover>
      {/* Mounted per open, so every open starts from the visit as it is now. */}
      {dialogOpen && <SaveAsOrderSetDialog {...props} open onOpenChange={setDialogOpen} />}
    </span>
  );
}
